import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Star, X, MessageCircleQuestion } from 'lucide-react';
import { reputation } from '../lib/api';
import { useAuth } from '../lib/auth';

/**
 * Small non-intrusive banner that surfaces pending review reminders — one
 * card per reminder. Dismiss removes it permanently (backend flag).
 *
 * Reminders are populated by the backend scheduler:
 *   • 24h after order.status = delivered
 *   • 1h  after Khata balance hits 0
 */
export default function ReviewReminderBanner() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);

  const load = useCallback(() => {
    if (!user) { setRows([]); return; }
    reputation.myReminders()
      .then(r => setRows(r.reminders || []))
      .catch(() => setRows([]));
  }, [user]);
  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  if (!user || rows.length === 0) return null;

  const dismiss = async (id) => {
    setRows(prev => prev.filter(r => r.id !== id));
    try { await reputation.dismissReminder(id); } catch { /* silent — reload will re-populate */ }
  };

  return (
    <div data-testid="review-reminder-banner" className="max-w-7xl mx-auto px-4 md:px-6 mt-4 space-y-2">
      {rows.slice(0, 3).map(r => (
        <div key={r.id} data-testid={`review-reminder-${r.id}`}
          className="card-soft p-3 md:p-4 flex items-center gap-3 border-l-4 border-[#E67E22] bg-[#FFF8EE]">
          <div className="w-9 h-9 rounded-full bg-[#F1C40F]/20 text-[#7A4A00] grid place-items-center">
            <Star className="w-5 h-5 fill-[#F1C40F] text-[#F1C40F]"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-sm truncate">{r.title}</div>
            <div className="text-xs text-ink/60 truncate">{r.body}</div>
          </div>
          {r.cta_url && (
            <Link to={r.cta_url} data-testid={`reminder-cta-${r.id}`}
              className="btn-primary !py-1.5 !px-3 !text-xs whitespace-nowrap">
              <MessageCircleQuestion className="w-3 h-3"/>Rate now
            </Link>
          )}
          <button data-testid={`reminder-dismiss-${r.id}`} onClick={() => dismiss(r.id)}
            className="text-ink/40 hover:text-ink/80 p-1" aria-label="Dismiss">
            <X className="w-4 h-4"/>
          </button>
        </div>
      ))}
    </div>
  );
}
